import { useState, useEffect, useCallback } from 'react';
import { useAppTheme } from '@/context/ThemeContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card_Widget';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, PieChart } from '@/components/charts';
import ExportButtons from '@/components/ExportButtons';
import type { CharityAnalytics as ExportCharityAnalytics } from '@/utils/export';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';

interface Volunteer {
  status: string;
  created_at: string;
}

interface Collection {
  created_at: string;
}

interface Event {
  date: string;
  attendees?: number;
  created_at: string;
  status: string;
}

interface CharityAnalytics {
  // Volunteer Statistics
  volunteerStats: {
    total: number;
    accepted: number;
    rejected: number;
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
  };
  // Collection Statistics
  collectionStats: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
    monthOverMonthChange: number;
  };
  // Event Statistics
  eventStats: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    thisYear: number;
    totalAttendees: number;
    thisMonthAttendees: number;
    lastMonthAttendees: number;
    monthOverMonthChange: number;
  };
  // Time-based distributions
  timeDistribution: {
    [key: string]: number;
  };
}

const TIME_PERIODS = [
  'This Month', 'Last Month', 'This Year'
];

export default async function AnalyticsDashboardCharity() {
  const { user } = useAuthV2();
  const { themeConfig } = useAppTheme();
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [filterPeriod, ] = useState('month'); // 'week', 'month', 'year'
  const [] = useState<string>('');
  const [canExport] = useState(false);
  const [stats, setStats] = useState<CharityAnalytics | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Create Supabase client
      const { createClient, normalizeUser } = await import('@/services/supabase');
      const supabase = createClient();
      
      // Get current user directly from Supabase
      const { data: { session } } = await (await supabase).auth.getSession();
      
      // Check if we have a valid session
      if (!session?.user) {
        console.error('No valid Supabase session found');
        setError('Authentication error. Please log in again.');
        setLoading(false);
        return;
      }
      
      // Use normalizeUser to get consistent user data
      const normalizedUser = normalizeUser(session.user);
      const charityId = normalizedUser.business_id; // charity_id is mapped to business_id in normalized user
      
      // Fetch volunteers data
      const { data: volunteers, error: volunteersError } = await (await supabase)
        .from('volunteers')
        .select('*')
        .eq('charity_id', charityId);

      // Fetch collections data
      const { data: collections, error: collectionsError } = await (await supabase)
        .from('collections')
        .select('*')
        .eq('charity_id', charityId);

      // Fetch events data
      const { data: events, error: eventsError } = await (await supabase)
        .from('events')
        .select('*')
        .eq('charity_id', charityId);

      if (volunteersError || collectionsError || eventsError) throw new Error('Error fetching data');

      // Process volunteer statistics
      const currentDate = new Date();
      const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const firstDayOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      const firstDayOfYear = new Date(currentDate.getFullYear(), 0, 1);

      const volunteerStats = {
        total: volunteers.length,
        accepted: volunteers.filter((v: Volunteer) => v.status === 'accepted').length,
        rejected: volunteers.filter((v: Volunteer) => v.status === 'rejected').length,
        thisMonth: volunteers.filter((v: Volunteer) => new Date(v.created_at) >= firstDayOfMonth).length,
        lastMonth: volunteers.filter((v: Volunteer) => 
          new Date(v.created_at) >= firstDayOfLastMonth && 
          new Date(v.created_at) < firstDayOfMonth
        ).length,
        thisYear: volunteers.filter((v: Volunteer) => new Date(v.created_at) >= firstDayOfYear).length
      };

      // Process collection statistics
      const collectionStats = {
        total: collections.length,
        thisMonth: collections.filter((c: Collection) => new Date(c.created_at) >= firstDayOfMonth).length,
        lastMonth: collections.filter((c: Collection) => 
          new Date(c.created_at) >= firstDayOfLastMonth && 
          new Date(c.created_at) < firstDayOfMonth
        ).length,
        thisYear: collections.filter((c: Collection) => new Date(c.created_at) >= firstDayOfYear).length,
        monthOverMonthChange: 0 // Will be calculated below
      };

      // Process event statistics
      const eventStats = {
        total: events.length,
        thisMonth: events.filter((e: Event) => new Date(e.date) >= firstDayOfMonth).length,
        lastMonth: events.filter((e: Event) => 
          new Date(e.date) >= firstDayOfLastMonth && 
          new Date(e.date) < firstDayOfMonth
        ).length,
        thisYear: events.filter((e: Event) => new Date(e.date) >= firstDayOfYear).length,
        totalAttendees: events.reduce((sum: number, e: Event) => sum + (e.attendees || 0), 0),
        thisMonthAttendees: events
          .filter((e: Event) => new Date(e.date) >= firstDayOfMonth)
          .reduce((sum: number, e: Event) => sum + (e.attendees || 0), 0),
        lastMonthAttendees: events
          .filter((e: Event) => 
            new Date(e.date) >= firstDayOfLastMonth && 
            new Date(e.date) < firstDayOfMonth
          )
          .reduce((sum: number, e: Event) => sum + (e.attendees || 0), 0),
        monthOverMonthChange: 0 // Will be calculated below
      };

      // Calculate month-over-month changes
      collectionStats.monthOverMonthChange = collectionStats.lastMonth > 0 
        ? ((collectionStats.thisMonth - collectionStats.lastMonth) / collectionStats.lastMonth) * 100 
        : 0;

      eventStats.monthOverMonthChange = eventStats.lastMonthAttendees > 0 
        ? ((eventStats.thisMonthAttendees - eventStats.lastMonthAttendees) / eventStats.lastMonthAttendees) * 100 
        : 0;

      setStats({
        volunteerStats,
        collectionStats,
        eventStats,
        timeDistribution: TIME_PERIODS.reduce((acc, period) => ({
          ...acc,
          [period]: period === 'This Month' ? volunteerStats.thisMonth :
                   period === 'Last Month' ? volunteerStats.lastMonth :
                   volunteerStats.thisYear
        }), {})
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterPeriod]);

  useEffect(() => {
    fetchData();
  }, [user, fetchData]);


  if (loading) return <div>Loading...</div>;
  if (!stats) return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <h1 className="text-xl font-medium text-navy-blue">Analytics Dashboard</h1>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <p className="text-gray-600">No data available</p>
        </div>
      </div>
    </div>
  );
  
  // Convert stats to the export format
  const exportData: ExportCharityAnalytics = stats as unknown as ExportCharityAnalytics;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-xl font-medium text-navy-blue">Analytics Dashboard</h1>
            </div>
            
            {/* Export Buttons - only enabled for admin and coordinator roles */}
            {await canExport ? (
              <ExportButtons
                data={exportData}
                title="Charity Analytics Report"
                subtitle="Performance Overview"
                type="analytics"
                filename="charity-analytics-report"
              />
            ) : (
              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none"
                  style={{
                    backgroundColor: '#9CA3AF',
                    borderRadius: '0.5rem',
                    cursor: 'not-allowed',
                    opacity: 0.7
                  }}
                  disabled
                  title="Only admins and coordinators can export reports"
                >
                  CSV
                </button>
                <button
                  className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none"
                  style={{
                    backgroundColor: '#9CA3AF',
                    borderRadius: '0.5rem',
                    cursor: 'not-allowed',
                    opacity: 0.7
                  }}
                  disabled
                  title="Only admins and coordinators can export reports"
                >
                  PDF
                </button>
              </div>
            )}
          </div>
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="volunteers">Volunteers</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Total Volunteers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.volunteerStats.total}</div>
                  <div className="text-sm" style={{ color: themeConfig.text }}>
                    {stats.volunteerStats.accepted} Accepted / {stats.volunteerStats.rejected} Rejected
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Total Collections</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.collectionStats.total}</div>
                  <div className="text-sm" style={{ color: themeConfig.text }}>
                    {stats.collectionStats.monthOverMonthChange > 0 ? '+' : ''}{stats.collectionStats.monthOverMonthChange.toFixed(1)}% vs last month
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Total Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-4xl font-bold" style={{ color: themeConfig.text }}>{stats.eventStats.total}</div>
                  <div className="text-sm" style={{ color: themeConfig.text }}>
                    {stats.eventStats.totalAttendees} Total Attendees
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>This Month's Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span style={{ color: themeConfig.text }}>Volunteers:</span>
                      <span style={{ color: themeConfig.text }}>{stats.volunteerStats.thisMonth}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: themeConfig.text }}>Collections:</span>
                      <span style={{ color: themeConfig.text }}>{stats.collectionStats.thisMonth}</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: themeConfig.text }}>Events:</span>
                      <span style={{ color: themeConfig.text }}>{stats.eventStats.thisMonth}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="volunteers" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Volunteer Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <PieChart
                    data={[
                      { name: 'Accepted', value: stats.volunteerStats.accepted },
                      { name: 'Rejected', value: stats.volunteerStats.rejected }
                    ]}
                  />
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Volunteer Trends</CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart
                    data={Object.entries(stats.timeDistribution).map(([period, count]) => ({
                      name: period,
                      value: count
                    }))}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="events" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Event Attendance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span style={{ color: themeConfig.text }}>This Month:</span>
                      <span style={{ color: themeConfig.text }}>{stats.eventStats.thisMonthAttendees} attendees</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: themeConfig.text }}>Last Month:</span>
                      <span style={{ color: themeConfig.text }}>{stats.eventStats.lastMonthAttendees} attendees</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: themeConfig.text }}>Change:</span>
                      <span style={{ 
                        color: stats.eventStats.monthOverMonthChange >= 0 ? 'green' : 'red'
                      }}>
                        {stats.eventStats.monthOverMonthChange > 0 ? '+' : ''}{stats.eventStats.monthOverMonthChange.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-md">
                <CardHeader>
                  <CardTitle style={{ color: themeConfig.primary }}>Event Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span style={{ color: themeConfig.text }}>This Month:</span>
                      <span style={{ color: themeConfig.text }}>{stats.eventStats.thisMonth} events</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: themeConfig.text }}>Last Month:</span>
                      <span style={{ color: themeConfig.text }}>{stats.eventStats.lastMonth} events</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: themeConfig.text }}>This Year:</span>
                      <span style={{ color: themeConfig.text }}>{stats.eventStats.thisYear} events</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </div>
  );
}